import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Grid,
    Stack,
    CircularProgress,
    Chip,
    Divider,
    TextField,
    Autocomplete,
    Card,
    CardContent,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    Tooltip,
    List,
    Container,
} from '@mui/material';
import { ArrowOutward, Search as SearchIcon } from '@mui/icons-material';
import { fetchSponsorBySlug, fetchSponsorMentions } from '../api/sponsors';
import { SponsorDetailMentions } from '../components/SponsorDetailMentions';
import { SponsorMention } from '../types';

type PodcastGroup = {
    podcast: SponsorMention['podcast'];
    mentions: SponsorMention[];
};

const SponsorDetail = () => {
    const { t } = useTranslation('explorar');
    const { sponsorId } = useParams<{ sponsorId: string }>();
    const [selectedPodcasts, setSelectedPodcasts] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<'title' | 'mentionCount' | 'episodeCount' | 'views'>(
        'mentionCount'
    );

    const {
        data: sponsorDetails,
        isLoading: sponsorLoading,
        error: sponsorError,
    } = useQuery({
        queryKey: ['sponsor', sponsorId],
        queryFn: () => fetchSponsorBySlug(sponsorId!),
        enabled: !!sponsorId,
        staleTime: 0,
    });

    const {
        data: mentions,
        isLoading: mentionsLoading,
        error: mentionsError,
    } = useQuery<{
        mentions: SponsorMention[];
        stats: {
            podcasts: Array<{
                _id: string;
                title: string;
                thumbnail?: string;
                mentions: number;
                timeOnAir: number;
                views: number;
                episodeCount: number;
            }>;
            totals: {
                totalTimeOnAir: number;
                totalViews: number;
                totalMentions: number;
            };
        };
    }>({
        queryKey: ['sponsor-mentions', sponsorId],
        queryFn: () => fetchSponsorMentions(sponsorId!),
        enabled: !!sponsorId,
        staleTime: 0,
    });

    const sponsor = sponsorDetails?.sponsor;

    const handleSort = (property: 'title' | 'mentionCount' | 'episodeCount' | 'views') => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Get unique podcast names and mention counts
    const podcastOptions = useMemo(() => {
        if (!mentions?.stats?.podcasts) return [];
        return mentions.stats.podcasts
            .map(podcast => ({
                title: podcast.title,
                mentionCount: podcast.mentions,
                thumbnail: podcast.thumbnail,
                views: podcast.views,
                episodeCount: podcast.episodeCount,
            }))
            .sort((a, b) => {
                if (orderBy === 'title') {
                    return order === 'asc'
                        ? a.title.localeCompare(b.title)
                        : b.title.localeCompare(a.title);
                }
                return order === 'asc'
                    ? (a[orderBy] || 0) - (b[orderBy] || 0)
                    : (b[orderBy] || 0) - (a[orderBy] || 0);
            });
    }, [mentions?.stats?.podcasts, order, orderBy]);

    // Filter podcasts based on selection
    const filteredPodcasts = useMemo(() => {
        if (selectedPodcasts.length === 0) return podcastOptions;
        return podcastOptions.filter(podcast => selectedPodcasts.includes(podcast.title));
    }, [podcastOptions, selectedPodcasts]);

    // Filter mentions based on selected podcasts and search term
    const filteredMentions = useMemo(() => {
        if (!mentions?.mentions) return [];

        let filtered = mentions.mentions;

        // Filter by selected podcasts
        if (selectedPodcasts.length > 0) {
            filtered = filtered.filter(mention => selectedPodcasts.includes(mention.podcast.title));
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(mention =>
                mention.episode?.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [mentions?.mentions, selectedPodcasts, searchTerm]);

    // Group mentions by podcast
    const groupedMentions = useMemo(() => {
        if (!filteredMentions) return new Map<string, PodcastGroup>();

        const groups = new Map<string, PodcastGroup>();
        filteredMentions.forEach((mention: SponsorMention) => {
            const podcastTitle = mention.podcast.title;
            if (!groups.has(podcastTitle)) {
                groups.set(podcastTitle, {
                    podcast: mention.podcast,
                    mentions: [],
                });
            }
            groups.get(podcastTitle)?.mentions.push(mention);
        });

        return groups;
    }, [filteredMentions]);

    if (sponsorLoading || mentionsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (sponsorError || mentionsError) {
        return (
            <Box sx={{ mt: 4 }}>
                <Typography color="error">
                    {t('sponsorDetail.loadError')}{' '}
                    {JSON.stringify(sponsorError || mentionsError)}
                </Typography>
            </Box>
        );
    }

    if (!sponsor) {
        return (
            <Box sx={{ mt: 4 }}>
                <Typography>{t('sponsorDetail.notFound')}</Typography>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Stack spacing={4} sx={{ pt: 2 }}>
                {/* Sponsor Header Section */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                    {/* Sponsor Image */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            flexShrink: 0,
                            width: { xs: '100%', lg: 'auto' },
                        }}
                    >
                        <Box
                            component="img"
                            src={sponsor.logo}
                            alt={sponsor.name}
                            sx={{
                                width: { xs: '100%', sm: '200px' },
                                height: { xs: 'auto', sm: '200px' },
                                aspectRatio: '1',
                                objectFit: 'cover',
                                maxHeight: '200px',
                                maxWidth: '200px',
                                borderRadius: '20px',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        />
                    </Box>

                    {/* Sponsor Info */}
                    <Box
                        sx={{
                            flex: 1,
                            pl: { xs: 0, lg: 3 },
                            pt: { xs: 3, lg: 0 },
                        }}
                    >
                        {/* Title */}
                        <Typography variant="h1" component="h1" sx={{ fontSize: '3rem' }}>
                            {sponsor.name}
                        </Typography>

                        {/* Categories and Website */}
                        <Stack direction="row" spacing={1} sx={{ pb: 1 }}>
                            {sponsor.categories?.map((category: string) => (
                                <Chip
                                    key={category}
                                    label={category}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                            {sponsor.website && (
                                <Chip
                                    icon={<ArrowOutward sx={{ fontSize: 16 }} />}
                                    label="Website"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    component="a"
                                    href={sponsor.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    clickable
                                    sx={{
                                        cursor: 'pointer',
                                        '& .MuiChip-icon': {
                                            order: 1,
                                            mr: '2px',
                                            ml: '-4px',
                                        },
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>
                </Box>

                <Divider />

                <Box>
                    <Typography variant="h2">{t('sponsorDetail.mentionsHeading')}</Typography>
                </Box>

                <Box>
                    <Stack spacing={3}>
                        <Typography variant="h3">{t('sponsorDetail.podcasts')}</Typography>
                        <Stack spacing={2}>
                            <Autocomplete
                                multiple
                                value={selectedPodcasts}
                                onChange={(_, newValue) => {
                                    setSelectedPodcasts(newValue);
                                }}
                                options={podcastOptions.map(p => p.title)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={t('sponsorDetail.filterPodcast')}
                                        placeholder={t('sponsorDetail.select')}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <SearchIcon
                                                        sx={{
                                                            color: 'text.secondary',
                                                            mr: 1,
                                                        }}
                                                    />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        '&:hover': {
                                            '& > fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    },
                                }}
                            />
                            <Card>
                                <CardContent>
                                    <Box sx={{ pb: 2 }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        <TableSortLabel
                                                            active={orderBy === 'title'}
                                                            direction={
                                                                orderBy === 'title' ? order : 'asc'
                                                            }
                                                            onClick={() => handleSort('title')}
                                                            sx={{ fontWeight: 'bold' }}
                                                        >
                                                            {t('sponsorDetail.podcast')}
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={t('sponsorDetail.verifiedMentions')}>
                                                            <TableSortLabel
                                                                active={orderBy === 'mentionCount'}
                                                                direction={
                                                                    orderBy === 'mentionCount'
                                                                        ? order
                                                                        : 'asc'
                                                                }
                                                                onClick={() =>
                                                                    handleSort('mentionCount')
                                                                }
                                                                sx={{ fontWeight: 'bold' }}
                                                            >
                                                                {t('sponsorDetail.mentions')}
                                                            </TableSortLabel>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={t('sponsorDetail.episodesWithMentions')}>
                                                            <TableSortLabel
                                                                active={orderBy === 'episodeCount'}
                                                                direction={
                                                                    orderBy === 'episodeCount'
                                                                        ? order
                                                                        : 'asc'
                                                                }
                                                                onClick={() =>
                                                                    handleSort('episodeCount')
                                                                }
                                                                sx={{ fontWeight: 'bold' }}
                                                            >
                                                                {t('sponsorDetail.episodes')}
                                                            </TableSortLabel>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={t('sponsorDetail.viewsMarch')}>
                                                            <TableSortLabel
                                                                active={orderBy === 'views'}
                                                                direction={
                                                                    orderBy === 'views' ? order : 'asc'
                                                                }
                                                                onClick={() => handleSort('views')}
                                                                sx={{ fontWeight: 'bold' }}
                                                            >
                                                                Views
                                                            </TableSortLabel>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredPodcasts.map(podcast => (
                                                    <TableRow
                                                        key={podcast.title}
                                                        sx={{
                                                            height: 50,
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover',
                                                            },
                                                            borderRadius: 2,
                                                        }}
                                                    >
                                                        <TableCell
                                                            component="th"
                                                            scope="row"
                                                            sx={{
                                                                borderTopLeftRadius: 8,
                                                                borderBottomLeftRadius: 8,
                                                            }}
                                                        >
                                                            <Stack
                                                                direction="row"
                                                                spacing={2}
                                                                alignItems="center"
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={podcast.thumbnail}
                                                                    alt={podcast.title}
                                                                    sx={{
                                                                        width: 40,
                                                                        height: 40,
                                                                        borderRadius: 1,
                                                                        objectFit: 'cover',
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body1"
                                                                    component="span"
                                                                >
                                                                    {podcast.title}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {podcast.mentionCount}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {podcast.episodeCount}
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                borderTopRightRadius: 8,
                                                                borderBottomRightRadius: 8,
                                                            }}
                                                        >
                                                            {podcast.views?.toLocaleString?.()}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Stack>
                </Box>

                <Box sx={{ minHeight: '30vh' }}>
                    <Stack spacing={3}>
                        <Typography variant="h3">{t('sponsorDetail.mentionsSection')}</Typography>
                        <Stack spacing={2}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 8 }}>
                                    <TextField
                                        fullWidth
                                        label={t('sponsorDetail.searchEpisode')}
                                        placeholder={t('sponsorDetail.searchPlaceholder')}
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <SearchIcon
                                                    sx={{
                                                        color: 'text.secondary',
                                                        mr: 1,
                                                    }}
                                                />
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                '&:hover': {
                                                    '& > fieldset': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Autocomplete
                                        multiple
                                        options={podcastOptions.map(podcast => podcast.title)}
                                        value={selectedPodcasts}
                                        onChange={(_, newValue) => setSelectedPodcasts(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('sponsorDetail.filterPodcast')}
                                                placeholder={t('sponsorDetail.selectPodcasts')}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '12px',
                                                        '&:hover': {
                                                            '& > fieldset': {
                                                                borderColor: 'primary.main',
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option}
                                                    label={option}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))
                                        }
                                        sx={{
                                            '& .MuiAutocomplete-tag': {
                                                margin: '2px',
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {groupedMentions.size > 0 ? (
                                <List disablePadding>
                                    {Array.from(groupedMentions.entries()).map(
                                        ([podcastTitle, group], groupIndex) => (
                                            <React.Fragment key={podcastTitle}>
                                                <SponsorDetailMentions mentions={group.mentions} />

                                                {groupIndex < groupedMentions.size - 1 && (
                                                    <Divider sx={{ my: 2 }} />
                                                )}
                                            </React.Fragment>
                                        )
                                    )}
                                </List>
                            ) : (
                                <Typography variant="body1" color="text.secondary">
                                    {selectedPodcasts.length > 0
                                        ? t('sponsorDetail.noMentionsForPodcasts')
                                        : t('sponsorDetail.noMentions')}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Stack>
        </Container>
    );
};

export default SponsorDetail;
